/**
 * RoomLayoutModal Component
 * Displays room layout and detailed information in a modal
 */

import { X, Bed, Users, Ruler, Home, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import GetAppModal from '../modals/GetAppModal';

const RoomLayoutModal = ({ room, layout, loading, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGetAppModal, setShowGetAppModal] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!room) return null;

  const imageList = room.images && room.images.length > 0 ? room.images : (room.image ? [room.image] : []);
  const currentImage = imageList[currentImageIndex];

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };

  const modalContent = (
    <>
      {/* Full screen backdrop - covers entire viewport */}
      <div 
        className="fixed top-0 left-0 w-screen h-screen bg-black/50 backdrop-blur-md z-40"
        onClick={onClose}
      />
      
      {/* Modal container */}
      <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-700 shadow-2xl">
          {/* Header with gradient */}
          <div className="sticky top-0 bg-gradient-to-r from-green-600 to-green-700 p-6 flex justify-between items-center z-10">
            <div className="flex items-center gap-3">
              <Home className="w-6 h-6 text-white" />
              <div>
                <h2 className="text-3xl font-bold text-white">Room {room.roomNumber}</h2>
                <p className="text-green-100 text-sm">Floor {room.floor} • {room.roomType}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8">
            {/* Room Image Gallery */}
            {imageList.length > 0 && (
              <div className="rounded-xl overflow-hidden shadow-lg group relative">
                <img
                  src={currentImage}
                  alt={`Room ${room.roomNumber}`}
                  className="w-full h-64 object-cover"
                />

                {/* Image Navigation */}
                {imageList.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>

                    {/* Image Thumbnails - Centered */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {imageList.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(idx);
                          }}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/70'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                <p className="text-gray-400 text-sm mb-1">Total Beds</p>
                <p className="text-2xl font-bold text-green-400">{room.beds?.length || room.capacity}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                <p className="text-gray-400 text-sm mb-1">Available</p>
                <p className="text-2xl font-bold text-blue-400">{room.availableBeds}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                <p className="text-gray-400 text-sm mb-1">Occupied</p>
                <p className="text-2xl font-bold text-orange-400">{room.occupiedBeds}</p>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                <p className="text-gray-400 text-sm mb-1">Monthly Rate</p>
                <p className="text-2xl font-bold text-green-400">Rs. {room.price}</p>
              </div>
            </div>

            {/* Dimensions */}
            {layout?.dimensions && (
              <div className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-xl p-6 border border-gray-600">
                <div className="flex items-center gap-3 mb-2">
                  <Ruler className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-semibold text-white">Room Dimensions</h3>
                </div>
                <p className="text-gray-300 text-lg">{layout.dimensions} units</p>
              </div>
            )}

            {/* Beds Section */}
            {layout?.beds && layout.beds.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Bed className="w-5 h-5 text-green-400" />
                  <h3 className="text-xl font-bold text-white">Available Beds</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {layout.beds.map((bed, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg p-4 border-2 transition-all ${
                        bed.status === 'Available'
                          ? 'bg-green-900/20 border-green-500'
                          : bed.status === 'Occupied'
                          ? 'bg-red-900/20 border-red-500'
                          : 'bg-yellow-900/20 border-yellow-500'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-bold text-lg">Bed {bed.bedNumber}</p>
                          <p className="text-gray-400 text-sm">Rs. {bed.monthlyRate}</p>
                        </div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold ${
                            bed.status === 'Available'
                              ? 'bg-green-500 text-white'
                              : bed.status === 'Occupied'
                              ? 'bg-red-500 text-white'
                              : 'bg-yellow-500 text-white'
                          }`}
                        >
                          {bed.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Furniture Section */}
            {layout?.furniture && layout.furniture.length > 0 && (
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Room Furniture</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {layout.furniture.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-700/50 rounded-lg p-3 border border-gray-600 text-center hover:border-green-500 transition-colors"
                    >
                      <p className="text-gray-300 text-sm capitalize font-medium">
                        {item.replace('-', ' ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {room.description && (
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <h3 className="text-lg font-semibold text-white mb-2">Description</h3>
                <p className="text-gray-300">{room.description}</p>
              </div>
            )}

            {/* Action Button */}
            {room.available && (
              <button 
                onClick={() => setShowGetAppModal(true)}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-4 rounded-lg transition-all transform hover:scale-105 shadow-lg text-lg"
              >
                Book This Room
              </button>
            )}

            {!room.available && (
              <div className="w-full bg-gray-700 text-gray-300 font-bold py-4 rounded-lg text-center text-lg">
                No Beds Available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Get App Modal */}
      <GetAppModal isOpen={showGetAppModal} onClose={() => setShowGetAppModal(false)} />
    </>
  );

  // Render as portal to body to escape parent overflow constraints
  return ReactDOM.createPortal(modalContent, document.body);
};

export default RoomLayoutModal;
